import { Component, computed, inject, input } from '@angular/core';
import { Router } from '@angular/router';
import { toSignal } from '@angular/core/rxjs-interop';
import { CartStoreDispatcher } from '@common/dispatchers/cart.dispatcher';
import { ContentStoreDispatcher } from '@common/dispatchers/content.dispatcher';
import { ProductStoreDispatcher } from '@common/dispatchers/product.dispatcher';
import { CartStoreRefiner } from '@common/refiners/cart.refiner';
import { ModalStoreSelector } from '@common/selectors/modal.selector';
import { OrderStoreSelector } from '@common/selectors/order.selector';
import {
  CartItemKeys,
  ReconstructedCartItemKeys,
  ReconstructedCartKeys,
} from '@config/keys/cart.keys';
import { DestroyerBase } from '@core/base/destroyer/destroyer.base';
import {
  DoodReconstructedCart,
  DoodReconstructedCartItem,
  ICartItem,
} from '@core/models/cart.model';
import { IContentBuilderFieldColor } from '@core/models/content-builder-fields.model';
import { DoodProductModel } from '@core/models/product.model';
import { CartService } from '@core/services/cart/cart.service';
import { KIOSK_SUGGESTIONS_MODAL_BLOCK_COMPONENT } from '@core/services/cb2-preview/cb2-preview-bloc-types';
import { Cb2PreviewStateService } from '@core/services/cb2-preview/cb2-preview-state.service';
import { previewScenarioData } from '@core/services/cb2-preview/preview-scenario-data.util';
import { previewScenarioForBloc$ } from '@core/services/cb2-preview/preview-scenario-for-bloc.util';
import { EventTrackerService } from '@core/services/event-tracker/event-tracker.service';
import { GetModalInterface, ModalConfig } from '@core/services/modals/modals-util.type';
import { ModalsService } from '@core/services/modals/modals.service';
import { suggestionProductOrigin } from '@core/models/suggestion-channel.util';
import { injectSuggestionsSetsService } from '@core/services/suggestions-sets/suggestions-sets.service';
import type { KioskSuggestionsModalBlockScenarioContent } from '@front-platform/content-builder';
import { Store } from '@ngrx/store';
import {
  KioskProductOverviewModalComponent,
  KioskProductOverviewModalConfig,
} from '@shared/modals/kiosk-product-overview-modal/kiosk-product-overview-modal.component';
import { ProductAddModalConfig } from '@shared/modals/product-add-modal/product-add-modal.component';
import { KioskGlobalSettingsService } from '@store/kiosk-global-settings/kiosk-global-settings.service';
import { PRODUCT_STORE_COMPONENT_ACTIONS } from '@store/product';
import { filter, map, shareReplay } from 'rxjs';
import { resolveKioskSuggestionsModalButtonLabel } from './kiosk-suggestions-modal-button-label.util';
import { resolveKioskSuggestionsProductsLayout } from './kiosk-suggestions-products-layout';
import { productMatchesCatalogFilters } from '@shared/blocks/shop-product-list-block/catalog-filters';
import { ShopCatalogFiltersService } from '@shared/blocks/shop-product-list-block/shop-catalog-filters.service';

type ProductModalWithCartItem = DoodProductModel & { cartItem?: ICartItem };

export type KioskSuggestionsModalConfig = ModalConfig<{
  handle: typeof KioskSuggestionsModalComponent.handle;
  inputs: {
    heading?: string;
    buttonBackgroundColor?: IContentBuilderFieldColor;
    buttonTextColor?: IContentBuilderFieldColor;
    buttonLabel?: string;
    textColor?: IContentBuilderFieldColor;
    backgroundColor?: IContentBuilderFieldColor;
    secondaryColor?: IContentBuilderFieldColor;
    cardColor?: IContentBuilderFieldColor;
    suggestionsSet?: string;
    products?: DoodProductModel[];
  };
  outputs: {};
}>;
@Component({
  selector: 'app-kiosk-suggestions-modal',
  templateUrl: './kiosk-suggestions-modal.component.html',
  standalone: false,
})
export class KioskSuggestionsModalComponent
  extends DestroyerBase
  implements GetModalInterface<KioskSuggestionsModalConfig>
{
  static handle = 'kiosk-suggestions-modal' as const;
  static heading = 'kiosk-suggestions-modal.heading';

  private readonly kioskGlobalSettingsService = inject(KioskGlobalSettingsService);
  private readonly router = inject(Router);
  private readonly catalogFilters = inject(ShopCatalogFiltersService);
  private readonly cartStoreRefiner = inject(CartStoreRefiner);
  private readonly modalStoreSelector = inject(ModalStoreSelector);
  private readonly cb2PreviewState = inject(Cb2PreviewStateService);
  private readonly previewScenario = toSignal(
    previewScenarioForBloc$(KIOSK_SUGGESTIONS_MODAL_BLOCK_COMPONENT),
    { initialValue: null },
  );

  protected readonly activeShopId = toSignal(
    this.cartService.getShopActive$().pipe(
      filter(shop => !!shop),
      map(shop => shop?.id as string),
      shareReplay(1),
    ),
  );

  protected readonly index = toSignal(
    this.modalStoreSelector
      .selectModal(KioskSuggestionsModalComponent.handle)
      .pipe(map(el => (el?.index ? el.index : 4))),
    {
      initialValue: 4,
    },
  );

  constructor(
    private readonly modalsService: ModalsService<
      [KioskSuggestionsModalConfig, KioskProductOverviewModalConfig, ProductAddModalConfig]
    >,
    private readonly cartService: CartService,
    private readonly eventTrackerService: EventTrackerService,
    private readonly contentDispatcher: ContentStoreDispatcher,
    private readonly cartStoreDispatcher: CartStoreDispatcher,
    private readonly productDispatcher: ProductStoreDispatcher,
    private readonly orderSelector: OrderStoreSelector,
    private readonly store: Store,
  ) {
    super();
  }

  public readonly heading = input<string>();
  protected readonly _heading = computed(() => this.heading() ?? 'kiosk-suggestions-modal.heading');
  public readonly buttonBackgroundColor = input<IContentBuilderFieldColor>();
  public readonly buttonTextColor = input<IContentBuilderFieldColor>();
  public readonly buttonLabel = input<string>();
  protected readonly _buttonLabel = computed(() =>
    resolveKioskSuggestionsModalButtonLabel(this.buttonLabel()),
  );
  public readonly textColor = input<IContentBuilderFieldColor>();
  public readonly backgroundColor = input<IContentBuilderFieldColor>();
  public readonly secondaryColor = input<IContentBuilderFieldColor>();
  public readonly cardColor = input<IContentBuilderFieldColor>();
  public readonly suggestionsSet = input<string>('');
  public readonly products = input<DoodProductModel[]>();

  private readonly suggestionsSetResource = injectSuggestionsSetsService({
    suggestionSetId: this.suggestionsSet,
  }).productsToSuggestBySetQuery.selectSignal(this.suggestionsSet);

  protected readonly suggestions = computed(() => {
    const products = this.resolveSuggestionProducts();
    const cartItems = this.cartStoreRefiner.selectActiveCartProductsSignal();
    const filters = this.catalogFilters.applied();
    return products
      ?.filter(product => !filters.length || productMatchesCatalogFilters(product, filters))
      .map(product => {
        const cartItem = cartItems.find(
          item =>
            item[CartItemKeys.ItemId] === product.id &&
            item[CartItemKeys.ShopId] === product.shop_id,
        );
        if (cartItem) {
          return { ...product, cartItem, quantity: cartItem.quantity };
        }
        return { ...product, quantity: 0 };
      });
  });

  private resolveSuggestionProducts(): DoodProductModel[] | undefined {
    const injectedProducts = this.products();
    if (injectedProducts !== undefined) {
      return injectedProducts;
    }
    if (this.cb2PreviewState.isActive()) {
      const content = previewScenarioData<KioskSuggestionsModalBlockScenarioContent>(
        this.previewScenario(),
        KIOSK_SUGGESTIONS_MODAL_BLOCK_COMPONENT,
      );
      if (content) {
        return content.suggestions as unknown as DoodProductModel[];
      }
    }
    return this.suggestionsSetResource().value;
  }

  protected incrementSimpleProductQuantityOrShowEditModal(product: ProductModalWithCartItem): void {
    this.store.dispatch(
      PRODUCT_STORE_COMPONENT_ACTIONS.selectProduct({
        productId: product.id,
        productShopId: product.shop_id,
        productOrigin: suggestionProductOrigin(
          this.router.url,
          this.products() !== undefined
            ? 'suggestion_set_cart-recommendation'
            : `suggestion_set_${this.suggestionsSet()}`,
        ),
      }),
    );
  }

  protected openProductDetailsModal(product: DoodProductModel): void {
    this.productDispatcher.insertActive(product);
    this.modalsService.open(KioskProductOverviewModalComponent.handle, undefined, {
      image: product.images?.cover?.url,
      name: product.name,
      description: product.description,
      allergens: product.allergens,
      type: product.type,
      colors: {
        closeButton: this.buttonBackgroundColor(),
        accent: this.secondaryColor(),
      },
    });
  }

  close(): void {
    this.modalsService.close(KioskSuggestionsModalComponent.handle);
  }

  valid(): void {
    this.close();
  }

  private lookForItemInCart(
    itemId: number,
    reconstructedCart: DoodReconstructedCart[],
  ): null | DoodReconstructedCartItem {
    for (const cartOfShop of reconstructedCart) {
      const cartItem = cartOfShop[ReconstructedCartKeys.Items].find(
        elem => elem[ReconstructedCartItemKeys.Item][CartItemKeys.Id] === itemId,
      );
      if (cartItem) {
        return cartItem;
      }
    }

    return null;
  }

  protected unAvailableProductById = toSignal(
    this.orderSelector.selectErrors.pipe(
      map(errors =>
        errors.violations
          ?.filter(
            violation =>
              violation.code &&
              [
                'PRODUCT_NOT_AVAILABLE_FOR_DISTRIBUTION_MODE',
                'PRODUCT_HIDDEN',
                'PRODUCT_NOT_AVAILABLE',
              ].includes(violation.code),
          )
          .map(violation => violation.data?.id as string)
          .reduce(
            (acc, id) => {
              acc[id] = true;
              return acc;
            },
            {} as Record<string, true>,
          ),
      ),
    ),
  );

  protected readonly productsLayout = computed(() =>
    resolveKioskSuggestionsProductsLayout({
      productCount: this.suggestions()?.length ?? 0,
      productCardChoice: this.kioskGlobalSettingsService.productCardChoice(),
    }),
  );
}
